/**
 * PWA display-mode helpers — pure functions, no Angular DI.
 *
 * The CSS `display-mode` media feature reports how the app is being
 * presented: `browser` for a normal tab, `standalone`/`minimal-ui`/
 * `window-controls-overlay`/`fullscreen` when running as an installed PWA.
 * Used for the `display_mode` analytics super-property and to hide the
 * install affordance once the app is already installed.
 */

const APP_DISPLAY_MODES = [
  'window-controls-overlay',
  'fullscreen',
  'standalone',
  'minimal-ui',
] as const;

export type DisplayMode = (typeof APP_DISPLAY_MODES)[number] | 'browser';

/** The current display mode, defaulting to 'browser' when undetectable. */
export function currentDisplayMode(): DisplayMode {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'browser';
  }
  for (const mode of APP_DISPLAY_MODES) {
    try {
      if (window.matchMedia(`(display-mode: ${mode})`).matches) return mode;
    } catch {
      // matchMedia can throw on exotic embedded webviews — treat as browser.
      return 'browser';
    }
  }
  // iOS Safari standalone (Add to Home Screen) predates the media feature.
  const nav = navigator as Navigator & { standalone?: boolean };
  if (nav.standalone === true) return 'standalone';
  return 'browser';
}

/** True when running as an installed app (any non-browser display mode). */
export function isStandaloneDisplay(): boolean {
  return currentDisplayMode() !== 'browser';
}
